// ---------------------------------------------------------------------------
// FeltonDiscovery -- Page Component (V11)
// ---------------------------------------------------------------------------
// Thin orchestrator composing all Discovery sub-components.
// Data fetching lives in useDiscoveryData, modal flows in
// useDiscoveryFilterModals, constants in discoveryConstants.
//
// State:
//   - URL-synced: period, dataSource (useExplorerUrlState), filters (useDiscoveryUrlFilters)
//   - Local: isTrendsOpen, viewMode, searchTerm, pagination, sort
// ---------------------------------------------------------------------------

import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Compass } from 'lucide-react';
import { useProfile } from '@/hooks/useProfile';
import type { Table } from '@tanstack/react-table';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  MoreVertical,
  Download,
  FolderOpen,
  Plus,
  TrendingUp,
  LayoutList,
  LayoutGrid,
} from 'lucide-react';
import { DataTable } from '@/components/v11/table';
import { periodToDates } from '@/lib/periodToDates';
import { cn } from '@/lib/utils';
import {
  DiscoveryToolbar,
  GlobalTimeBar,
  TrendsPulseBoard,
  DataLoader,
  DrillDownBadge,
  getDrillDownRelation,
  getFilterDimensionForDrillDown,
  DRILL_DOWN_RELATIONS,
  ActiveFilterChips,
} from '@/components/v11/discovery';
import { useTrendsPulseData } from '@/hooks/useTrendsPulseData';
import { periodToGranularity } from '@/lib/periodToGranularity';
import type { VanityMetric, GroupByDimension, TimePeriod } from '@/components/v11/discovery';
import type { MetricSummary } from '@/components/v11/discovery/TrendsPulseBoard';
import type { ViewMode, DataSourceId } from '@/components/v11/discovery';
import type { TableMeta, DrillDownCellParams } from '@/components/v11/table/types';
import { LoadPersonaModal } from '@/components/v11/funnel/modals/LoadPersonaModal';
import { DimensionPickerModal } from '@/components/v11/funnel/modals/DimensionPickerModal';
import { ValuePickerModal } from '@/components/v11/funnel/modals/ValuePickerModal';
import { useDiscoveryUrlFilters } from '@/hooks/useDiscoveryUrlFilters';
import { useV11Layout } from '@/contexts/V11LayoutContext';
import { useExplorerUrlState } from '@/hooks/useExplorerUrlState';
import { generateCsv } from '@/components/v11/discovery/ExportButton';
import { COLUMNS_BY_SOURCE } from '@/components/v11/discovery/constants';
import { ChartViewGrid } from '@/components/v11/charts';

import {
  DEFAULT_PAGE_SIZE,
  DEFAULT_SORT,
  DEFAULT_SORT_BY_SOURCE,
  COLUMN_TO_SORT_FIELD_MAP,
  getColumnKeyFromSortField,
} from './discoveryConstants';
import { useDiscoveryData } from './useDiscoveryData';
import { useDiscoveryFilterModals } from './useDiscoveryFilterModals';

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const FeltonDiscovery: React.FC = () => {
  // Layout context
  const { setHeaderControls } = useV11Layout();
  const { data: userProfile } = useProfile();
  const accountId = userProfile?.accountHealth?.accountId;

  // Direct access to setSearchParams for atomic multi-operation URL updates
  const [, setSearchParams] = useSearchParams();

  // URL-synced state
  const {
    state: urlState,
    setPeriod,
    setCustomDates,
    clearCustomDates,
    setDataSource,
    setDrillDown,
    clearDrillDown,
  } = useExplorerUrlState();

  // Derived URL values
  const dataSource = urlState.dataSource;
  const period = urlState.period;
  const customDateFrom = urlState.dateFrom;
  const customDateTo = urlState.dateTo;
  const drillSource = urlState.drillSource;
  const drillId = urlState.drillId;
  const drillLabel = urlState.drillLabel;
  const hasDrillDown = Boolean(drillSource && drillId && drillLabel);

  // Local UI state
  const [searchTerm, setSearchTerm] = useState('');
  const [isTrendsOpen, setIsTrendsOpen] = useState(false);
  const [trendsMetric, setTrendsMetric] = useState<VanityMetric>('likes');
  const [trendsGroupBy, setTrendsGroupBy] = useState<GroupByDimension>('platform');
  const [viewMode, setViewMode] = useState<ViewMode>('table');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [sortBy, setSortBy] = useState<string>(
    () => DEFAULT_SORT_BY_SOURCE[dataSource] || DEFAULT_SORT
  );
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const tableRef = useRef<Table<unknown> | null>(null);
  const [visibleColumnKeys, setVisibleColumnKeys] = useState<ReadonlyArray<string>>([]);

  // Ref for tracking datasource changes (each consumer gets its own)
  const prevDataSourceForColumnsRef = useRef<DataSourceId>(dataSource);
  const prevDataSourceForFiltersRef = useRef<DataSourceId>(dataSource);
  const prevDataSourceForSortRef = useRef<DataSourceId>(dataSource);

  // -----------------------------------------------------------------------
  // Drill-down
  // -----------------------------------------------------------------------

  const activeDrillDownRelation = useMemo(() => {
    if (!drillSource || !drillId) return null;
    const relations = ['posts', 'comments'];
    for (const col of relations) {
      const relation = getDrillDownRelation(drillSource, col);
      if (relation && relation.target === dataSource) return relation;
    }
    return null;
  }, [drillSource, drillId, dataSource]);

  // -----------------------------------------------------------------------
  // Period and filters
  // -----------------------------------------------------------------------

  const periodDates = useMemo(() => {
    if (period === 'custom' && customDateFrom && customDateTo) {
      return { date_from: customDateFrom, date_to: customDateTo };
    }
    return periodToDates(period);
  }, [period, customDateFrom, customDateTo]);

  const drillDownFilter = useMemo(() => {
    if (!hasDrillDown || !activeDrillDownRelation || !drillId) return undefined;
    return { [activeDrillDownRelation.apiParam]: drillId };
  }, [hasDrillDown, activeDrillDownRelation, drillId]);

  const {
    filters: urlFilters,
    filterChips,
    apiFilters,
    includeOwnChannels,
    setIncludeOwnChannels,
    addFilter,
    removeFilter,
    toggleFilter,
    clearAll,
    loadFilters,
    retainApplicableOnSourceChange,
  } = useDiscoveryUrlFilters({ dataSource, periodDates, drillDownFilter, searchTerm, accountId });

  // -----------------------------------------------------------------------
  // Filter-based column masking (hide avatar/username when non-platform
  // filters are active to avoid misleading identity associations)
  // -----------------------------------------------------------------------

  const IDENTITY_COLUMNS = ['avatar', 'username'] as const;
  const IDENTITY_REPLACEMENT_COLUMNS = ['id'] as const;

  const hasNonPlatformFilters = useMemo(() => {
    return urlFilters.some(f => f.dimension !== 'platform' && f.dimension !== 'channel' && f.enabled !== false);
  }, [urlFilters]);

  const shouldMaskIdentity = dataSource === 'profiles' && hasNonPlatformFilters;

  // When masked: hide avatar/username, show id
  // When not masked: hide id, show avatar/username (restore defaults)
  const forcedHidden = useMemo(() => {
    if (dataSource === 'brands') return ['totalDetections'];
    if (dataSource === 'teams') return ['totalMentions'];
    if (dataSource !== 'profiles') return undefined;
    return shouldMaskIdentity ? [...IDENTITY_COLUMNS] : [...IDENTITY_REPLACEMENT_COLUMNS];
  }, [shouldMaskIdentity, dataSource]);

  const forcedVisible = useMemo(() => {
    if (dataSource !== 'profiles') return undefined;
    return shouldMaskIdentity ? [...IDENTITY_REPLACEMENT_COLUMNS] : undefined;
  }, [shouldMaskIdentity, dataSource]);

  const toolbarFilters = useMemo(() => {
    if (!searchTerm) return filterChips;
    return [
      {
        id: '__search__',
        label: 'Search',
        value: `"${searchTerm}"`,
        category: 'search',
        enabled: true,
      },
      ...filterChips,
    ];
  }, [filterChips, searchTerm]);

  // -----------------------------------------------------------------------
  // Data hook (queries, accumulators, derived state)
  // -----------------------------------------------------------------------

  const {
    currentData,
    currentError,
    currentTotal,
    hasMorePages,
    isInitialLoading,
    isFetchingMore,
    isFetchingCurrent,
    chartData,
    isLoadingChart,
    chartTotalRecords,
    trendsData,
    isLoadingTrends,
    trendsError,
    filterOptions,
    currentColumns,
    searchPlaceholder,
    aggregationSummary,
    isLoadingAggregationSummary,
  } = useDiscoveryData({
    dataSource,
    period,
    apiFilters,
    currentPage,
    pageSize,
    sortBy,
    sortOrder,
    isTrendsOpen,
    trendsMetric,
    trendsGroupBy,
    customDateFrom: customDateFrom ?? undefined,
    customDateTo: customDateTo ?? undefined,
  });

  // -----------------------------------------------------------------------
  // Trends Pulse Board data (parallel metric summaries + focused chart)
  // -----------------------------------------------------------------------

  const granularity = useMemo(
    () => periodToGranularity(period as TimePeriod, customDateFrom, customDateTo),
    [period, customDateFrom, customDateTo]
  );

  const {
    summaries: trendsSummaries,
    isLoadingSummaries: isLoadingTrendsSummaries,
    focusedData: trendsFocusedData,
    isLoadingFocused: isLoadingTrendsFocused,
    focusedError: trendsFocusedError,
  } = useTrendsPulseData({
    dataSource,
    apiFilters,
    focusedMetric: trendsMetric,
    groupBy: trendsGroupBy,
    isOpen: isTrendsOpen,
    granularity,
  });

  // -----------------------------------------------------------------------
  // Filter modals hook
  // -----------------------------------------------------------------------

  const modals = useDiscoveryFilterModals({
    filterOptions,
    addFilter,
    loadFilters,
    urlFilters,
    dateRange: periodDates,
  });

  // -----------------------------------------------------------------------
  // Effects (datasource change, page reset, trends defaults)
  // -----------------------------------------------------------------------

  // Reset visible columns when datasource changes
  useEffect(() => {
    if (prevDataSourceForColumnsRef.current !== dataSource) {
      const defaultColumns = COLUMNS_BY_SOURCE[dataSource]
        .filter((c: { defaultVisible?: boolean }) => c.defaultVisible)
        .map((c: { key: string }) => c.key);
      setVisibleColumnKeys(defaultColumns);
      prevDataSourceForColumnsRef.current = dataSource;
    }
  }, [dataSource]);

  // Retain applicable filters on datasource change
  useEffect(() => {
    if (prevDataSourceForFiltersRef.current !== dataSource) {
      retainApplicableOnSourceChange(dataSource);
      prevDataSourceForFiltersRef.current = dataSource;
    }
  }, [dataSource, retainApplicableOnSourceChange]);

  // Reset sort to datasource default when datasource changes
  useEffect(() => {
    if (prevDataSourceForSortRef.current !== dataSource) {
      setSortBy(DEFAULT_SORT_BY_SOURCE[dataSource] || DEFAULT_SORT);
      setSortOrder('desc');
      prevDataSourceForSortRef.current = dataSource;
    }
  }, [dataSource]);

  // Reset trends defaults when datasource changes
  useEffect(() => {
    const defaultMetrics: Record<string, VanityMetric> = {
      profiles: 'new_profiles',
      content: 'likes',
      comments: 'comments',
      brands: 'total_detections',
      teams: 'total_mentions',
      hashtags: 'posts_count',
    };
    setTrendsMetric(defaultMetrics[dataSource] || 'likes');
    setTrendsGroupBy('platform');
  }, [dataSource]);

  // Reset page when filters/sort change
  useEffect(() => {
    setCurrentPage(1);
  }, [period, apiFilters, sortBy, sortOrder]);

  // Inject GlobalTimeBar into layout header
  useEffect(() => {
    setHeaderControls(
      <GlobalTimeBar
        selectedPeriod={period}
        onPeriodChange={setPeriod}
        customDateFrom={customDateFrom}
        customDateTo={customDateTo}
        onCustomDatesChange={setCustomDates}
        onCustomDatesClear={clearCustomDates}
      />
    );
    return () => setHeaderControls(null);
  }, [
    period,
    setPeriod,
    customDateFrom,
    customDateTo,
    setCustomDates,
    clearCustomDates,
    setHeaderControls,
  ]);

  // -----------------------------------------------------------------------
  // Handlers
  // -----------------------------------------------------------------------

  const handleDataSourceChange = useCallback(
    (source: DataSourceId) => {
      setSortBy(DEFAULT_SORT_BY_SOURCE[source] || DEFAULT_SORT);
      setSortOrder('desc');
      setDataSource(source, { clearDrillDown: true });
      setSearchTerm('');
      setCurrentPage(1);
    },
    [setDataSource]
  );

  const handleRemoveFilter = useCallback(
    (filterId: string) => {
      if (filterId === '__search__') {
        setSearchTerm('');
        return;
      }
      removeFilter(filterId);
    },
    [removeFilter]
  );

  const handleClearAll = useCallback(() => {
    setSearchTerm('');
    // Combine filter + drill-down clearing in a single atomic URL update
    // to avoid race condition between separate setSearchParams calls
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      // Clear all filter params (f_* prefix) and disabled state
      const keysToDelete = Array.from(next.keys()).filter(k => k.startsWith('f_'));
      for (const key of keysToDelete) {
        next.delete(key);
      }
      next.delete('fd');
      next.delete('fdv');
      // Clear drill-down params
      next.delete('drillSource');
      next.delete('drillId');
      next.delete('drillLabel');
      return next;
    });
  }, [setSearchParams]);

  const handleExport = useCallback(() => {
    if (currentData.length === 0) return;
    const columnConfigs = (
      COLUMNS_BY_SOURCE as Record<DataSourceId, readonly { key: string; label: string }[]>
    )[dataSource];
    const columns = columnConfigs.map(c => ({ key: c.key, label: c.label }));
    const csv = generateCsv(currentData, columns);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `felton-${dataSource}-export.csv`;
    link.click();
    URL.revokeObjectURL(url);
  }, [currentData, dataSource]);

  const handleToggleTrends = useCallback(() => setIsTrendsOpen(prev => !prev), []);
  const handleViewModeChange = useCallback((mode: ViewMode) => setViewMode(mode), []);

  const handleTableReady = useCallback((table: Table<unknown>) => {
    tableRef.current = table;
    setVisibleColumnKeys(table.getVisibleLeafColumns().map(col => col.id));
  }, []);

  const handleColumnVisibilityChange = useCallback((keys: string[]) => {
    setVisibleColumnKeys(keys);
  }, []);

  const handlePaginationChange = useCallback(
    (state: { pageIndex: number; pageSize: number }) => {
      queueMicrotask(() => {
        if (state.pageSize !== pageSize) {
          setPageSize(state.pageSize);
          setCurrentPage(1);
        } else {
          setCurrentPage(state.pageIndex + 1);
        }
      });
    },
    [pageSize]
  );

  const handleSortChange = useCallback(
    (sorting: Array<{ id: string; desc: boolean }>) => {
      queueMicrotask(() => {
        if (sorting.length > 0) {
          const { id, desc } = sorting[0];
          setSortBy(COLUMN_TO_SORT_FIELD_MAP[dataSource]?.[id] || id);
          setSortOrder(desc ? 'desc' : 'asc');
          setCurrentPage(1);
        } else {
          setSortBy(DEFAULT_SORT_BY_SOURCE[dataSource] || DEFAULT_SORT);
          setSortOrder('desc');
          setCurrentPage(1);
        }
      });
    },
    [dataSource]
  );

  const handleLoadMore = useCallback(() => setCurrentPage(prev => prev + 1), []);

  const handleSearchChange = useCallback((search: string) => {
    setSearchTerm(search);
    setCurrentPage(1);
  }, []);

  const handleDrillDown = useCallback(
    (params: DrillDownCellParams) => {
      setSortBy(DEFAULT_SORT);
      setSortOrder('desc');
      setCurrentPage(1);
      setSearchTerm('');

      // Find the relation to check if it has a filter dimension mapping
      const relation = DRILL_DOWN_RELATIONS.find(
        r =>
          r.source === dataSource &&
          r.target === params.targetDataSource &&
          r.apiParam === params.filterKey
      );

      const dimensionConfig = relation?.filterDimensionKey
        ? getFilterDimensionForDrillDown(dataSource, relation.sourceColumn)
        : undefined;

      if (dimensionConfig) {
        // Unified path: atomic URL update — set source + add filter + clear drill-down
        // Must be a single setSearchParams call to avoid the second overwriting the first
        setSearchParams(prev => {
          const next = new URLSearchParams(prev);
          // 1. Switch datasource
          next.set('source', params.targetDataSource);
          // 2. Clear drill-down params
          next.delete('drillSource');
          next.delete('drillId');
          next.delete('drillLabel');
          // 3. Clear ALL existing filters so Content shows the exact posts count
          //    (cross-view filters have different semantics per datasource and would
          //    reduce the result set, causing a mismatch with the displayed count)
          const keysToDelete = Array.from(next.keys()).filter(k => k.startsWith('f_'));
          for (const key of keysToDelete) {
            next.delete(key);
          }
          next.delete('fd');
          next.delete('fdv');
          // 4. Add the drill-down filter as the only active filter
          next.set(`f_${dimensionConfig.key}`, String(params.filterValue));
          next.set(`f_${dimensionConfig.key}_labels`, params.displayLabel);
          return next;
        });
      } else {
        // Legacy path: use DrillDownBadge (e.g., profiles → content with author_id)
        setDrillDown({
          sourceDataSource: dataSource,
          targetDataSource: params.targetDataSource,
          filterId: String(params.filterValue),
          filterLabel: params.displayLabel,
        });
      }
    },
    [dataSource, setDrillDown, setSearchParams]
  );

  const handleClearDrillDown = useCallback(() => clearDrillDown(), [clearDrillDown]);

  const tableMeta = useMemo<TableMeta<unknown>>(
    () => ({
      onDrillDown: handleDrillDown,
      currentDataSource: dataSource,
    }),
    [handleDrillDown, dataSource]
  );

  // Initial sorting state for the DataTable (to show sort arrow on load)
  // Uses the default sort for the dataSource to ensure correct arrow on mount
  const initialSorting = useMemo(() => {
    const defaultSortField = DEFAULT_SORT_BY_SOURCE[dataSource] || DEFAULT_SORT;
    const columnKey = getColumnKeyFromSortField(dataSource, defaultSortField);
    return [{ id: columnKey, desc: true }];
  }, [dataSource]);

  // Contextual empty state message
  const hasActiveFilters = filterChips.length > 0 || searchTerm.length > 0;
  const isShortPeriod = period === '7d' || period === '30d';
  const emptyMessage = useMemo(() => {
    if (hasActiveFilters && isShortPeriod) {
      return `No ${dataSource} found for the selected filters in the last ${period === '7d' ? '7 days' : '30 days'}. Try expanding the time period or adjusting your filters.`;
    }
    if (isShortPeriod) {
      return `No ${dataSource} found in the last ${period === '7d' ? '7 days' : '30 days'}. Try a longer time period.`;
    }
    if (hasActiveFilters) {
      return `No ${dataSource} match your current filters. Try removing some filters or broadening your search.`;
    }
    return `No ${dataSource} found for the selected period.`;
  }, [dataSource, hasActiveFilters, isShortPeriod, period]);

  const handleExpandPeriod = useCallback(() => {
    setPeriod('12m');
  }, [setPeriod]);

  // -----------------------------------------------------------------------
  // Enriched summaries (inject total_profiles from aggregations for profiles)
  // -----------------------------------------------------------------------

  const enrichedSummaries = useMemo((): readonly MetricSummary[] => {
    if (dataSource !== 'profiles') return trendsSummaries;
    const totalProfiles = chartTotalRecords || 0;

    // Borrow sparkline & half-period values from new_profiles so the card
    // renders with the same mini-chart and % variation as the other cards.
    const newProfilesSummary = trendsSummaries.find(s => s.metric === 'new_profiles');

    const totalProfilesSummary: MetricSummary = {
      metric: 'total_profiles',
      currentValue: totalProfiles,
      previousValue: newProfilesSummary?.previousValue ?? 0,
      recentHalfValue: newProfilesSummary?.recentHalfValue ?? 0,
      sparkline: newProfilesSummary?.sparkline ?? [],
    };
    return [totalProfilesSummary, ...trendsSummaries];
  }, [dataSource, trendsSummaries, chartTotalRecords]);

  // -----------------------------------------------------------------------
  // Render
  // -----------------------------------------------------------------------

  return (
    <div className="space-y-3">
      {/* Row 1: Page Header + View Mode + DataSource + Kebab Menu */}
      <div className="flex items-center gap-2">
        <div className="p-2 bg-purple-500/10 rounded-lg border border-purple-500/20">
          <Compass className="h-5 w-5 text-purple-500" />
        </div>
        <h1 className="text-lg font-bold text-v11-text-primary">Discovery</h1>

        <div className="flex items-center gap-2 ml-auto">
          <DiscoveryToolbar selectedSource={dataSource} onSourceChange={handleDataSourceChange} />

          {/* Kebab Menu */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                className="border-v11-border text-v11-text-secondary hover:bg-v11-surface-hover"
                data-testid="discovery-more-menu"
              >
                <MoreVertical className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={modals.openLoadPersona} data-testid="menu-load-persona">
                <FolderOpen className="h-4 w-4 mr-2" />
                Load Persona
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={handleExport}
                disabled={currentData.length === 0}
                data-testid="menu-export"
              >
                <Download className="h-4 w-4 mr-2" />
                Export
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Row 2: Add Filter CTA + separator + filter chips + Clear all */}
      <div className="flex items-center gap-2 flex-wrap">
        {/* Blue filled CTA — primary action for filtering */}
        <Button
          size="sm"
          onClick={modals.openAddFilter}
          data-testid="add-filter-button"
          className="bg-primary text-primary-foreground hover:bg-primary/90 shadow-sm transition-colors"
        >
          <Plus className="h-3.5 w-3.5" />
          <span className="text-xs font-medium">Add Filter</span>
        </Button>

        {/* Show Own Channels toggle (profiles and comments) */}
        {(dataSource === 'profiles' || dataSource === 'comments') && (
          <>
            <div className="h-5 w-px bg-v11-border mx-0.5" />
            <div className="flex items-center gap-1.5">
              <Switch
                id="include-own-channels"
                checked={includeOwnChannels}
                onCheckedChange={setIncludeOwnChannels}
                className="scale-75"
              />
              <span
                className="text-xs text-v11-text-secondary cursor-pointer select-none"
                onClick={() => setIncludeOwnChannels(!includeOwnChannels)}
              >
                Show Own Channels
              </span>
            </div>
          </>
        )}

        {/* Separator between CTA and chips */}
        {(toolbarFilters.length > 0 || hasDrillDown) && (
          <div className="h-5 w-px bg-v11-border mx-0.5" />
        )}

        {/* Active filter chips */}
        <ActiveFilterChips
          filters={toolbarFilters}
          onRemoveFilter={handleRemoveFilter}
          onToggleFilter={toggleFilter}
        />
        {hasDrillDown && activeDrillDownRelation && drillLabel && (
          <DrillDownBadge
            sourceLabel={activeDrillDownRelation.labelPrefix}
            displayValue={drillLabel}
            onClear={handleClearDrillDown}
          />
        )}

        {/* Clear all — text link on the right */}
        {toolbarFilters.length + (hasDrillDown ? 1 : 0) >= 1 && (
          <button
            type="button"
            onClick={handleClearAll}
            data-testid="clear-all-filters"
            title="Clear all filters"
            className="text-xs text-red-400 hover:text-red-300 transition-colors"
          >
            Clear all
          </button>
        )}
      </div>

      {/* Trends Pulse Board (collapsible) */}
      {isTrendsOpen && (
        <TrendsPulseBoard
          summaries={enrichedSummaries}
          focusedData={trendsFocusedData as any}
          dataSource={dataSource}
          isOpen={isTrendsOpen}
          isLoading={isLoadingTrendsSummaries || isLoadingTrendsFocused}
          error={trendsFocusedError?.message}
          focusedMetric={trendsMetric}
          onFocusedMetricChange={m => setTrendsMetric(m as VanityMetric)}
          groupBy={trendsGroupBy}
          onGroupByChange={g => setTrendsGroupBy(g as GroupByDimension)}
        />
      )}

      {/* Aggregation Summary Bar — distinct background band + view toggle */}
      <div className="flex items-center gap-3 text-xs px-6 py-2.5 -mx-6 bg-v11-surface border-y border-v11-border">
        {/* Stats (left side) */}
        {aggregationSummary && !isLoadingAggregationSummary && (
          <>
            {aggregationSummary.stats.map(stat => (
              <div key={stat.label} className="flex items-center gap-1.5">
                <span className="font-semibold text-v11-text-primary tabular-nums">
                  {stat.value}
                </span>
                <span className="text-v11-text-muted">{stat.label}</span>
              </div>
            ))}
            {aggregationSummary.topPlatforms.length > 0 && (
              <>
                <div className="h-4 w-px bg-v11-border" />
                {aggregationSummary.topPlatforms.map(p => (
                  <div key={p.name} className="flex items-center gap-1 text-v11-text-muted">
                    <span>{p.name}</span>
                    <span className="font-medium text-v11-text-secondary tabular-nums">
                      {p.percentage}%
                    </span>
                  </div>
                ))}
              </>
            )}
          </>
        )}

        {/* View mode toggle (right side) — segmented control */}
        <div className="flex items-center ml-auto bg-v11-bg-secondary border border-v11-border rounded-lg p-0.5 gap-0.5">
          <button
            type="button"
            onClick={handleToggleTrends}
            aria-label="Toggle trends panel"
            aria-pressed={isTrendsOpen}
            className={cn(
              'flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs font-medium transition-colors',
              isTrendsOpen
                ? 'bg-primary/15 text-primary shadow-sm'
                : 'text-v11-text-muted hover:text-v11-text-secondary hover:bg-v11-surface-hover'
            )}
          >
            <TrendingUp className="h-3.5 w-3.5" />
            Trends
          </button>
          <button
            type="button"
            onClick={() => handleViewModeChange('table')}
            aria-label="Switch to table view"
            aria-pressed={viewMode === 'table'}
            className={cn(
              'flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs font-medium transition-colors',
              viewMode === 'table'
                ? 'bg-primary/15 text-primary shadow-sm'
                : 'text-v11-text-muted hover:text-v11-text-secondary hover:bg-v11-surface-hover'
            )}
          >
            <LayoutList className="h-3.5 w-3.5" />
            Table
          </button>
          <button
            type="button"
            onClick={() => handleViewModeChange('chart')}
            aria-label="Switch to chart view"
            aria-pressed={viewMode === 'chart'}
            className={cn(
              'flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs font-medium transition-colors',
              viewMode === 'chart'
                ? 'bg-primary/15 text-primary shadow-sm'
                : 'text-v11-text-muted hover:text-v11-text-secondary hover:bg-v11-surface-hover'
            )}
          >
            <LayoutGrid className="h-3.5 w-3.5" />
            Charts
          </button>
        </div>
      </div>

      {/* Table View */}
      {viewMode === 'table' && (
        <>
          {isInitialLoading ? (
            <DataLoader dataSource={dataSource} variant="initial" />
          ) : currentError ? (
            <div className="rounded-xl border border-red-500/20 bg-red-500/5 p-8 text-center">
              <p className="text-sm font-medium text-red-400">
                Failed to load {dataSource} data. Please try again.
              </p>
              <p className="mt-1 text-xs text-v11-text-muted">
                {currentError instanceof Error ? currentError.message : 'Unknown error'}
              </p>
            </div>
          ) : (
            <div className="relative">
              {isFetchingMore && (
                <div className="absolute inset-0 z-50 flex items-start justify-center pt-20 bg-v11-bg/60 backdrop-blur-sm">
                  <DataLoader dataSource={dataSource} variant="loadMore" />
                </div>
              )}
              <div
                className={cn('transition-opacity duration-200', isFetchingCurrent && 'opacity-50')}
              >
                <DataTable
                  key={dataSource}
                  data={currentData as Record<string, unknown>[]}
                  columns={currentColumns as any}
                  forcedHiddenColumns={forcedHidden}
                  forcedVisibleColumns={forcedVisible}
                  enableSorting
                  manualSorting={true}
                  initialSorting={initialSorting}
                  onSortChange={handleSortChange}
                  enablePagination={false}
                  enableSearch
                  searchPlaceholder={searchPlaceholder}
                  manualFiltering={true}
                  onSearchChange={handleSearchChange}
                  enableColumnVisibility
                  tableId={`felton-explorer-${dataSource}`}
                  emptyMessage={emptyMessage}
                  emptyActionLabel={
                    isShortPeriod
                      ? 'Expand to 12 months'
                      : hasActiveFilters
                        ? 'Clear all filters'
                        : undefined
                  }
                  onEmptyAction={
                    isShortPeriod
                      ? handleExpandPeriod
                      : hasActiveFilters
                        ? handleClearAll
                        : undefined
                  }
                  onTableReady={handleTableReady}
                  onColumnVisibilityChange={handleColumnVisibilityChange}
                  maxHeight="calc(100vh - 280px)"
                  stickyColumns={dataSource === 'profiles' && !shouldMaskIdentity ? 2 : 1}
                  meta={tableMeta}
                  footer={
                    currentData.length > 0 && visibleColumnKeys.length > 0 ? (
                      <div className="border-t border-v11-border">
                        <div className="px-4 py-3 flex items-center justify-between">
                          <span className="text-sm text-v11-text-secondary">
                            Showing {currentData.length.toLocaleString()}
                            {chartTotalRecords != null && chartTotalRecords > currentData.length
                              ? ` of ${chartTotalRecords.toLocaleString()}`
                              : ''}{' '}
                            results
                          </span>
                          {hasMorePages && (
                            <button
                              onClick={handleLoadMore}
                              disabled={isFetchingCurrent}
                              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-v11-surface-hover hover:bg-v11-surface-active text-v11-text-primary text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              {isFetchingCurrent ? (
                                <>
                                  <div className="h-4 w-4 animate-spin rounded-full border-2 border-primary border-t-transparent" />
                                  <span>Loading...</span>
                                </>
                              ) : (
                                <span>Load More Results</span>
                              )}
                            </button>
                          )}
                        </div>
                      </div>
                    ) : undefined
                  }
                />
              </div>
            </div>
          )}
        </>
      )}

      {/* Chart View */}
      {viewMode === 'chart' && (
        <>
          {visibleColumnKeys.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-20 px-4 text-center">
              <div className="mb-4 p-4 rounded-full bg-v11-surface-hover">
                <svg
                  className="h-12 w-12 text-v11-text-muted"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                  />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-v11-text-primary mb-2">
                No Charts to Display
              </h3>
              <p className="text-sm text-v11-text-muted max-w-md">
                Select at least one column from the table to view its chart visualization. Click the{' '}
                <span className="font-medium text-v11-text-secondary">Columns</span> button in the
                table view to enable columns.
              </p>
            </div>
          ) : (
            <ChartViewGrid
              data={chartData as ReadonlyArray<Record<string, unknown>>}
              columns={COLUMNS_BY_SOURCE[dataSource]}
              visibleColumnKeys={visibleColumnKeys}
              isLoading={isLoadingChart}
              totalRecords={chartTotalRecords}
            />
          )}
        </>
      )}

      <LoadPersonaModal
        open={modals.isLoadPersonaOpen}
        onClose={modals.closeLoadPersona}
        onSelect={modals.selectPersona as any}
        personas={modals.personas as any}
        isLoading={modals.isLoadingPersonas}
        onDelete={modals.deletePersona}
        isDeleting={modals.isDeletingPersona}
      />

      <DimensionPickerModal
        open={modals.isDimensionModalOpen}
        onClose={modals.closeDimensionModal}
        onSelectDimension={modals.selectDimension}
        usedDimensions={modals.usedDimensions}
        datasource={dataSource}
        onRequestCounts={modals.requestDimensionCounts}
      />

      <ValuePickerModal
        open={modals.isValueModalOpen}
        onClose={modals.closeValueModal}
        dimensionLabel={modals.selectedDimension?.label ?? ''}
        values={modals.dimensionValues}
        isLoading={modals.isLoadingValues}
        searchMode={modals.isSearchMode}
        onSearch={modals.handleSearch}
        isSearching={modals.isSearching}
        onApply={modals.applyValues}
        initialSelected={modals.initialSelectedValues}
      />
    </div>
  );
};
